import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ordersService } from "@/services/ordersService";
import type { OrderFilterParams, OrderStatusUpdateDto } from "@/types/order.dto";
import {
  mapOrderListDtosToAccountRows,
  mapOrderListDtosToAdminOrders,
} from "@/lib/mappers/order-mappers";
import { queryKeys } from "./keys";

export function useMyOrders(filter: OrderFilterParams = { pageSize: 100 }) {
  return useQuery({
    queryKey: queryKeys.orders.my(filter),
    queryFn: () => ordersService.getMyOrders(filter),
  });
}

export function useAccountOrders(filter: OrderFilterParams = { pageSize: 100 }) {
  return useQuery({
    queryKey: queryKeys.orders.my(filter),
    queryFn: async () => {
      const result = await ordersService.getMyOrders(filter);
      return {
        ...result,
        items: mapOrderListDtosToAccountRows(result.items),
      };
    },
  });
}

export function useAllOrders(filter: OrderFilterParams = { pageSize: 100 }) {
  return useQuery({
    queryKey: queryKeys.orders.all(filter),
    queryFn: () => ordersService.getAll(filter),
  });
}

export function useAdminOrders(filter: OrderFilterParams = { pageSize: 100 }) {
  return useQuery({
    queryKey: queryKeys.orders.all(filter),
    queryFn: async () => {
      const result = await ordersService.getAll(filter);
      return {
        ...result,
        items: mapOrderListDtosToAdminOrders(result.items),
      };
    },
  });
}

export function useOrderById(id: number, enabled = true) {
  return useQuery({
    queryKey: queryKeys.orders.detail(id),
    queryFn: () => ordersService.getById(id),
    enabled: enabled && id > 0,
  });
}

export function useOrderByNumber(orderNumber: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.orders.byNumber(orderNumber),
    queryFn: () => ordersService.getByNumber(orderNumber),
    enabled: enabled && orderNumber.length > 0,
  });
}

export function useCreateOrderMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ordersService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: queryKeys.wallets.my });
    },
  });
}

export function useCancelOrderMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => ordersService.cancel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}

export function useUpdateOrderStatusMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: OrderStatusUpdateDto }) =>
      ordersService.updateStatus(id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}
